import {
  Component,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  OnInit,
} from '@angular/core';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { NavButtonComponent } from '../nav-button/nav-button';
import { FormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { SelectComponent } from '../select/selectComponent';

@Component({
  selector: 'app-additional-info-popup',
  templateUrl: './additional-info-popup.html',
  styleUrls: ['./additional-info-popup.scss'],
  imports: [NavButtonComponent, FormsModule, SelectComponent, NzIconModule, NzModalModule],
})
export class AdditionalInfoPopupComponent implements OnInit {
  public i18n = inject(I18nService);
  private ui = inject(UiService);

  addInfo_open = input<boolean>(false);
  infoTypeID = input<number>(1);
  infoTypeName = input<string>();
  addInfoValues = input<any[]>([]);
  isSteamTrap = input<boolean>(false);

  saveEvent = output<void>();
  cancelEvent = output<void>();
  private readonly PRICE_PER_TON_ID = 18;
  private readonly STEAM_RATE_CURRENCY_ID = 17;
  showPopupErrormessage = signal(false);
  steamRateCurrencyUnit = signal('€');
  steamRateCurrencyMDUnit = signal('EUR');
  isFieldUnTouched = signal(true);

  isMobile = computed(() => this.ui.IsMobile);
  isTablet = computed(() => this.ui.IsTablet);
  isDesktop = computed(() => this.ui.IsDesktop);
  showRequiredHint = computed(() => {
    const values = this.addInfoValues();
    if (!Array.isArray(values)) return false;
    return values.some((item) => item.infoTypeID === this.infoTypeID() && !item.optional);
  });
  addInfoList = signal<any[]>([]);
  private syncEffect = effect(() => {
    this.addInfoList.set(this.addInfoValues() ?? []);
  });
  refreshAddInfoList() {
    this.addInfoList.set([...this.addInfoList()]);
  }

  formInValid = computed(() => {
    const values = this.addInfoList();

    if (!Array.isArray(values)) return false;

    return values.some((item) => this.fieldInvalid(item));
  });

  ngOnInit() {
    const steamRateCurrency = this.addInfoValues().find(
      (v) => v.categoryID === this.STEAM_RATE_CURRENCY_ID
    );

    if (steamRateCurrency?.text) {
      const parsed = JSON.parse(steamRateCurrency.text);
      this.steamRateCurrencyUnit.set(parsed.en);
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(e: any): void {
    if (e?.target?.scrollTop !== undefined) {
      const topheight = this.addInfoValues().length > 3 ? 450 : 40;
      if (e.target.scrollTop > topheight) {
        this.showPopupErrormessage.set(true);
      }
    }
  }

  fieldInvalid(item: any): boolean {
    if (item.fieldType === 'radio') return false;

    const isEmpty =
      (!item.text || item.text.trim() === '') && (item.value === null || item.value === undefined);

    const isRequiredAndEmpty = item.infoTypeID === this.infoTypeID() && !item.optional && isEmpty;

    const hasValue = item.value !== null && item.value !== undefined;

    const exceedsMax = hasValue && item.maxValue != null && item.value > item.maxValue;

    const belowMin = hasValue && item.minValue != null && item.value < item.minValue;

    return isRequiredAndEmpty || exceedsMax || belowMin;
  }

  getUnitPrefix(prefix: string) {
    const parsed = prefix ? JSON.parse(prefix) : { mt: '', im: '' };    
    return parsed[this.i18n.measurementSystemSignal()];
  }

  getUnit(unit: any) {
    const parsed = unit || { mt: '', im: '' };
    const system = this.i18n.measurementSystemSignal();    
    if (parsed[system]?.includes('/ton')) {
      const tonInfo = this.addInfoValues().find((v) => v.categoryID === this.PRICE_PER_TON_ID);
      if (tonInfo) {
        tonInfo.unit = {
          mt: `${this.steamRateCurrencyUnit()}/ton`,
          im: `${this.steamRateCurrencyUnit()}/ton`,
          md: `${this.steamRateCurrencyMDUnit()}/ton`,
        };
      }
      return `${this.steamRateCurrencyUnit()}/ton`;
    }
    return parsed[system];
  }

  onFocusInEvent(addInfoValue: any) {
    addInfoValue.showValidation = true;
    addInfoValue.textValidation = false;
    this.isFieldUnTouched.set(false);
  }

  onFocusOutEvent(data: any) {
    const isValid = !this.isTextError(data) && !!data.value;
    data.showValidation = isValid;
    data.textValidation = isValid;
  }

  onOptionsSelected(addInfoValue: any, event: any) {
    if (addInfoValue.categoryID === this.STEAM_RATE_CURRENCY_ID) {
      const parsed = JSON.parse(event);
      this.steamRateCurrencyUnit.set(parsed.en);
      this.steamRateCurrencyMDUnit.set(parsed.md);
    }
  }

  addFieldClick(addInfoValue: any) {
    addInfoValue.multipleValues.push('');
  }

  deleteFieldClick(addInfoValue: any, i: number) {
    addInfoValue.multipleValues.splice(i, 1);
  }

  trackByFn(index: number) {
    return index;
  }

  private isTextError(addInfoValue: any) {
    return addInfoValue.validations?.every((v: any) => this.isValid(addInfoValue, v)) ?? true;
  }

  isValid(addInfoValue: any, validation: any) {
    const val = addInfoValue.value;
    const prog = addInfoValue.valueInProgess;

    if ((!!val && prog != null && prog.length === 0) || !val) {
      validation.validated = false;
      return false;
    }

    validation.validated = true;
    switch (validation.code) {
      case 0:
        return (prog !== null ? prog : val) >= 0;
      case 1:
        return val >= addInfoValue.minValue && val <= addInfoValue.maxValue;
      case 2:
        return (prog !== null ? prog : val) % 1 === 0;
      default:
        return false;
    }
  }
}
